import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeftRight, CheckCircle2, RefreshCw, ShieldCheck, Trash2, Zap } from "lucide-react";
import { timeAgo } from "./ReplicaBanner";
import { CollectionsTable, ReconcileSummary, RecentRuns, Stat, fmt, num } from "./SpokeSyncParts";
import { WipeReplicaDialog, WipeStatusLine } from "./WipeReplicaDialog";

const sum = (obj, keys) => Object.values(obj || {}).reduce((a, m) => a + keys.reduce((b, k) => b + (m?.[k] || 0), 0), 0);

/** Spoke (replica or linked standalone): two-way sync status, Sync now, Reconcile now. */
export function SpokeStatusCard({ status, onRefresh, canWrite }) {
    const [busy, setBusy] = useState(null);
    const [wiping, setWiping] = useState(false);
    if (!status) return null;
    const trigger = async (reconcile) => {
        setBusy(reconcile ? "reconcile" : "sync");
        try {
            await api.post("/admin/sync/run", { reconcile });
            toast.success(reconcile ? "Reconciliation requested — runs within 30 s" : "Sync requested — runs within 30 s");
            setTimeout(() => onRefresh?.(), 4000);
        } catch (e) {
            toast.error(`Request failed: ${formatApiErrorDetail(e?.response?.data?.detail)}`);
        } finally {
            setBusy(null);
        }
    };
    const healthy = !status.last_error;
    const push = status.last_push || {};
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6" data-testid="replica-status-card">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div className="min-w-0">
                    <div className="label-xs text-sky-300 inline-flex items-center gap-1.5"><ArrowLeftRight className="w-3 h-3" /> Two-way sync · {status.mode}</div>
                    <div className="font-display text-lg tracking-tight mt-1 break-all">{status.env_name} <span className="text-white/40">⇄</span> {status.hub_url}</div>
                    <div className="text-xs text-white/50 mt-1">
                        channel <span className="text-white/80" data-testid="replica-channel">{status.channel || "—"}</span> · every {Math.round((status.interval_seconds || 300) / 60)} min ·
                        bootstrap <span className="text-white/80" data-testid="replica-bootstrap">{status.bootstrap}</span> · hub wins on conflict
                        {status.running && <span className="ml-2 text-sky-300" data-testid="replica-running">syncing…</span>}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button onClick={onRefresh} className="inline-flex items-center gap-1.5 rounded border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5" data-testid="replica-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
                    <button onClick={() => trigger(true)} disabled={!!busy || status.running} className="inline-flex items-center gap-1.5 rounded border border-sky-400/40 px-3 py-1.5 text-xs text-sky-100 hover:bg-sky-400/10 disabled:opacity-50" data-testid="replica-reconcile-btn"><ShieldCheck className="w-3.5 h-3.5" /> Reconcile now</button>
                    <button onClick={() => trigger(false)} disabled={!!busy || status.running} className="inline-flex items-center gap-1.5 rounded bg-sky-500/80 hover:bg-sky-500 px-3 py-1.5 text-xs text-white disabled:opacity-50" data-testid="replica-sync-now-btn"><Zap className="w-3.5 h-3.5" /> Sync now</button>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <Stat label="Last sync" value={timeAgo(status.last_sync_at)} sub={fmt(status.last_sync_at)} testId="replica-last-sync" />
                <Stat label="Lag" value={num(status.lag)} sub={`applied ${num(status.applied_seq)} / hub ${num(status.visible_seq)}`} testId="replica-lag" tone={status.lag > 0 ? "text-amber-200" : ""} />
                <Stat label="Pushed to hub" value={num(push.sent)} sub={`last push · +${num(push.added)} ~${num(push.updated)} · hub won ${num(push.hub_won)}`} testId="replica-pushed" />
                <Stat label="Tracks" value={num(status.counts?.ripeness_tracks)} sub={`${num(status.counts?.ripeness_evaluations)} evaluations · ${num(status.pending_forwarded_batches)} fwd batches`} testId="replica-tracks" />
            </div>
            <div className={`flex items-start gap-2 rounded border px-3 py-2 text-xs mb-5 ${healthy ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-100" : "border-amber-400/40 bg-amber-400/10 text-amber-100"}`} data-testid="replica-health">
                {healthy ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
                <span className="break-all">{healthy ? `Sync healthy — local changes go up, hub changes come down (${num(sum(status.recent_runs?.[0]?.merged, ["added", "updated"]))} pulled last run).` : `Last sync failed: ${status.last_error}`}</span>
            </div>
            <ReconcileSummary status={status} />
            <div className="grid md:grid-cols-2 gap-6 mt-5">
                <CollectionsTable status={status} />
                <RecentRuns runs={status.recent_runs} />
            </div>
            <WipeSection status={status} canWrite={canWrite} onOpen={() => setWiping(true)} />
            {wiping && (
                <WipeReplicaDialog envName={status.env_name} onClose={() => setWiping(false)} onConfirm={async (confirm_name, collections) => {
                    try {
                        await api.post("/admin/sync/replica/wipe", { confirm_name, collections });
                        toast.success(`${collections ? "Collection refresh" : "Delete + re-download"} requested — runs within 30 s`);
                        setTimeout(() => onRefresh?.(), 4000);
                    } catch (e) {
                        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
                        throw e;
                    }
                }} />
            )}
        </div>
    );
}

function WipeSection({ status, canWrite, onOpen }) {
    const active = ["pushing", "deleting", "downloading"].includes(status.last_wipe?.status);
    return (
        <div className="mt-6 rounded border border-rose-400/20 bg-rose-400/[0.03] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="replica-wipe-section">
            <div className="min-w-0 text-xs">
                <div className="text-rose-100 font-medium">Delete Radar replica data</div>
                <div className="text-white/50 mt-0.5">Sends unsynced changes to the hub, deletes this environment's synced Radar data — all of it or one collection, e.g. evidence — then downloads a fresh copy.</div>
                <div className="mt-1"><WipeStatusLine wipe={status.last_wipe} pending={!!status.requested?.wipe_requested_at} pendingCollections={status.requested?.wipe_collections} testId="replica-wipe-status" /></div>
            </div>
            {canWrite ? (
                <button onClick={onOpen} disabled={active || !!status.requested?.wipe_requested_at}
                        className="inline-flex items-center gap-1.5 rounded border border-rose-400/50 px-3 py-1.5 text-xs text-rose-100 hover:bg-rose-400/10 disabled:opacity-40 transition-colors"
                        data-testid="replica-wipe-btn">
                    <Trash2 className="w-3.5 h-3.5" /> Delete and re-download
                </button>
            ) : <span className="text-[11px] text-white/40" data-testid="replica-wipe-superadmin-note">Super admin only</span>}
        </div>
    );
}
